import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CONTACT_INFO } from "../data";
import BeforeAfterSlider from "../components/BeforeAfterSlider";
import ContactForm from "../components/ContactForm";

export default function Epoxygulve() {
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fallback images for Epoxygulve
  const fallbackImages = [
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/hero-epoxy-floor.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-epoxy.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/epoxy-gallery-1.jpg",
    "https://assets.auxodev.dk/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/epoxy-gallery-2.jpg"
  ];

  useEffect(() => {
    async function fetchGallery() {
      try {
        const res = await fetch("https://api.dev.auxo.dk/public/by-slug/da/epoxygulve");
        if (res.ok) {
          const data = await res.json();
          setGallery(data);
        }
      } catch (err) {
        console.error("Fejl ved hentning af galleri fra CMS:", err);
      } finally {
        setLoading(false);
      }
    }
    fetchGallery();
  }, []);

  const galleryImages = gallery?.images || fallbackImages;
  const galleryTitle = gallery?.title?.da || "Epoxygulve";
  const galleryDesc = gallery?.description?.da || "Se vores udvalg af slidstærke og elegante epoxygulve til både erhverv, industri og private boliger.";

  return (
    <>
      <Helmet>
        <title>Epoxygulve Halsnæs | Slidstærke & Rengøringsvenlige Gulve</title>
        <meta
          name="description"
          content="Få lagt et professionelt epoxygulv i Halsnæs, Frederiksværk og Hundested. Slidstærke og fugefrie løsninger til private, garager, erhverv og industri."
        />
        <meta property="og:title" content="Epoxygulve Halsnæs | Slidstærke & Rengøringsvenlige Gulve" />
        <meta
          property="og:description"
          content="Få lagt et professionelt epoxygulv i Halsnæs, Frederiksværk og Hundested. Slidstærke og fugefrie løsninger til private, garager, erhverv og industri."
        />
        <meta property="og:image" content={fallbackImages[0]} />
      </Helmet>

      {/* Page Header (Hero Style) */}
      <section className="bg-brand-dark text-white pt-48 pb-32 md:pt-44 md:pb-28 relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src={fallbackImages[0]}
            alt="Epoxygulv baggrund"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-brand-dark via-brand-dark/90 to-transparent"></div>
        
        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <span className="text-xs md:text-sm font-extrabold uppercase tracking-widest text-brand-orange mb-3 block">
              Slidstærke & Fugefrie Gulve
            </span>
            <h1 className="text-3xl md:text-5xl font-extrabold uppercase tracking-tight text-white leading-tight">
              Professionelle Epoxygulve
            </h1>
            <p className="text-base md:text-lg text-slate-300 mt-4 max-w-2xl leading-relaxed">
              Få et fugefrit, slidstærkt og utrolig rengøringsvenligt epoxygulv udført af lokale specialister. Vi leverer skræddersyede løsninger til både erhverv, industri og private garager.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href="#kontakt-form"
                className="inline-flex items-center justify-center bg-brand-orange text-white px-6 py-3 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
              >
                Få et tilbud
              </a>
              <a
                href={`tel:+45${CONTACT_INFO.phone}`}
                className="inline-flex items-center justify-center bg-white text-brand-dark px-6 py-3 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-slate-100 transition-all"
              >
                Ring {CONTACT_INFO.phone}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content & Details */}
      <section className="py-20 bg-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            
            {/* Left: Detailed Copy */}
            <div className="lg:col-span-7 space-y-8">
              <div className="prose prose-slate max-w-none">
                <span className="text-xs md:text-sm font-extrabold uppercase tracking-widest text-brand-orange block mb-2">
                  Hvad er et epoxygulv?
                </span>
                <h2 className="text-3xl md:text-4xl font-extrabold text-brand-dark leading-snug mb-6">
                  Uovertruffen styrke og fugefri finish
                </h2>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">
                  Et epoxygulv er en fugefri gulvbelægning, der opbygges ved at blande en epoxyharpiks med en hærder. Når materialerne reagerer kemisk med hinanden, dannes der en ekstremt hård, slidstærk og uigennemtrængelig plastoverflade. Epoxy hæfter fantastisk på underlag som beton og flydemørtel.
                </p>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">
                  Hos Halsnæs Epoxygulve tilpasser vi altid belægningen til det specifikke formål. Vi kan regulere overfladens struktur, så du får præcis den skridsikkerhed, der kræves i dit arbejdsmiljø, eller den glans og farve, der passer til dit showroom eller din private garage.
                </p>

                <h3 className="text-xl font-bold text-brand-dark mt-8 mb-4">
                  Fordele ved vores epoxygulve:
                </h3>
                <ul className="space-y-3 my-6">
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Ekstrem holdbarhed:</strong> Modstår tung mekanisk belastning, kørsel med maskiner og dagligt slid i årtier.</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Fugefri og hygiejnisk:</strong> Ingen samlinger eller fuger, hvor skidt, støv og bakterier kan samle sig.</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Kemikalieresistent:</strong> Tåler spild af olie, benzin, syrer, opløsningsmidler og skrappe rengøringsmidler.</span>
                  </li>
                  <li className="flex items-start gap-3 text-slate-600">
                    <i className="fa-solid fa-circle-check text-brand-orange mt-1"></i>
                    <span><strong>Nemt at vedligeholde:</strong> Den glatte overflade gør den daglige rengøring utrolig hurtig og nem.</span>
                  </li>
                </ul>

                <h3 className="text-xl font-bold text-brand-dark mt-8 mb-4">
                  Epoxygulve til private garager og kældre
                </h3>
                <p className="text-sm md:text-base text-slate-600 leading-relaxed mb-6">
                  Epoxy er ikke længere kun forbeholdt industrien. Flere og flere private vælger at få lagt et flot epoxygulv i garagen, kælderen, bryggerset eller vaskehuset. Det giver et moderne, råt og minimalistisk udtryk, der samtidig er utrolig praktisk, da det ikke suger væske eller skidt til sig.
                </p>
              </div>

              {/* Before/After Gallery Section */}
              <div className="pt-8 border-t border-slate-100">
                <h3 className="text-2xl font-extrabold text-brand-dark mb-2">
                  Før & Efter Epoxybehandling
                </h3>
                <p className="text-sm text-slate-500 mb-6">
                  Træk i skyderen for at se den markante forskel før og efter vores professionelle epoxybehandling.
                </p>
                <BeforeAfterSlider 
                  beforeImage="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00005_4.jpeg"
                  afterImage="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/image00004_2.jpeg"
                />
              </div>
            </div>

            {/* Right: Sidebar & Contact Form */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-24" id="kontakt-form">
              <div className="bg-slate-50 p-8 rounded-lg border border-slate-100 shadow-sm">
                <span className="text-xs font-extrabold uppercase tracking-widest text-brand-orange block mb-2">
                  Få et gratis tilbud
                </span>
                <h3 className="text-2xl font-extrabold text-brand-dark mb-4">
                  Modtag uforpligtende tilbud
                </h3>
                <p className="text-sm text-slate-500 mb-6">
                  Udfyld formularen herunder, så vender Simon Babiel tilbage til dig hurtigst muligt med rådgivning og et skarpt tilbud.
                </p>
                <ContactForm source="Ydelsesside: Epoxygulve" />
              </div>

              <div className="bg-brand-teal text-white p-8 rounded-lg">
                <h4 className="text-lg font-bold mb-2">Professionel rådgivning</h4>
                <p className="text-sm text-slate-200 mb-4">
                  Har du spørgsmål til epoxygulve, lagtykkelser eller farvemuligheder? Ring direkte til Simon Babiel.
                </p>
                <div className="flex flex-col gap-2">
                  <a
                    href={`tel:+45${CONTACT_INFO.phone}`}
                    className="flex items-center gap-2 text-sm font-bold hover:text-brand-orange transition-colors"
                  >
                    <i className="fa-solid fa-phone"></i>
                    Tlf: {CONTACT_INFO.phone}
                  </a>
                  <a
                    href={`mailto:${CONTACT_INFO.email}`}
                    className="flex items-center gap-2 text-sm font-bold hover:text-brand-orange transition-colors break-all"
                  >
                    <i className="fa-solid fa-envelope"></i>
                    {CONTACT_INFO.email}
                  </a>
                </div>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Bottom CTA Block */}
      <section className="relative text-white py-24 overflow-hidden">
        {/* Background Image with Dark Overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=85,format=auto/assets/5961f5ff-ebf1-44e6-b9b5-afb30420abff/service-epoxy.jpg"
            alt="Slidstærkt epoxygulv"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-brand-dark/60"></div>
        </div>

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-extrabold uppercase tracking-tight mb-4">
            Skal vi lægge dit nye epoxygulv?
          </h2>
          <p className="text-base text-slate-200 max-w-2xl mx-auto mb-8">
            Vi udfører epoxy-opgaver for både erhverv, industri og private på hele Sjælland. Kontakt os i dag for en uforpligtende snak om dit projekt.
          </p>
          <a
            href="#kontakt-form"
            className="inline-flex items-center justify-center bg-brand-orange text-white px-8 py-4 text-sm font-bold uppercase tracking-wider rounded-full hover:bg-brand-orange/90 transition-all"
          >
            Kontakt os nu
          </a>
        </div>
      </section>
    </>
  );
}
